import { Alert, Button } from '@/ui';

import highlighterIcon from './icons/format_ink_highlighter.svg';
import psychiatryIcon from './icons/psychiatry.svg';
import volunteerActivismIcon from './icons/volunteer_activism.svg';

import type { CSSProperties } from 'react';

export const MEMBER_REFLECTION_ONBOARDING_COPY =
  'After each session, you’ll see a reflection like this where you can:';

export interface ReflectionOnboardingCalloutProps {
  copy?: string;
  dismissError?: string;
  isDismissing?: boolean;
  onDismiss?: () => void;
}

const calloutStyles: CSSProperties = {
  background: '#FBF5EC',
  borderRadius: 16,
  boxSizing: 'border-box',
  margin: '0 0 32px',
  marginLeft: '50%',
  padding: 40,
  transform: 'translateX(-50%)',
  width: 'min(752px, calc(100vw - 32px))',
};

const eyebrowStyles: CSSProperties = {
  color: '#637A6F',
  fontFamily: "'Basis Grotesque', sans-serif",
  fontSize: 12,
  fontWeight: 500,
  lineHeight: 1.334,
  margin: 0,
};

const titleStyles: CSSProperties = {
  color: '#262626',
  fontFamily: "'Basis Grotesque', sans-serif",
  fontSize: 32,
  fontWeight: 700,
  letterSpacing: 0,
  lineHeight: 1.2,
  margin: '7px 0 0',
  maxWidth: 578,
};

const copyStyles: CSSProperties = {
  color: '#262626',
  fontFamily: "'Basis Grotesque', sans-serif",
  fontSize: 16,
  fontWeight: 500,
  letterSpacing: 0.15,
  lineHeight: 1.5,
  margin: 0,
};

const listStyles: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 11,
  marginTop: 11,
};

const listItemStyles: CSSProperties = {
  alignItems: 'center',
  display: 'flex',
  gap: 16,
};

const iconStyles: CSSProperties = {
  flex: '0 0 24px',
  height: 24,
  width: 24,
};

const buttonRowStyles: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: 24,
};

const dismissButtonStyles: CSSProperties = {
  borderColor: '#A4A4A4',
  borderRadius: 12,
  color: '#262626',
  fontFamily: "'Basis Grotesque', sans-serif",
  fontSize: 15,
  fontWeight: 700,
  letterSpacing: 0.4,
  lineHeight: '26px',
  minWidth: 0,
  padding: '8px 22px',
  textTransform: 'none',
};

export const ReflectionOnboardingCallout = ({
  copy = MEMBER_REFLECTION_ONBOARDING_COPY,
  dismissError,
  isDismissing = false,
  onDismiss,
}: ReflectionOnboardingCalloutProps) => (
  <section aria-label="Reflection introduction" style={calloutStyles}>
    <p style={eyebrowStyles}>Session Reflection</p>
    <h2 style={titleStyles}>
      A lot happens in a coaching session. This is where it keeps going.
    </h2>
    <div style={{ marginTop: 24 }}>
      <p style={copyStyles}>{copy}</p>
      <div style={listStyles}>
        <div style={listItemStyles}>
          <img alt="" src={psychiatryIcon} style={iconStyles} />
          <p style={copyStyles}>
            Reflect on the themes, where your thinking shifted, and what you
            committed to.
          </p>
        </div>
        <div style={listItemStyles}>
          <img alt="" src={volunteerActivismIcon} style={iconStyles} />
          <p style={copyStyles}>Find your coach’s guidance and questions.</p>
        </div>
        <div style={listItemStyles}>
          <img alt="" src={highlighterIcon} style={iconStyles} />
          <p style={copyStyles}>
            Highlight and save the parts that mattered to you.
          </p>
        </div>
      </div>
    </div>
    {dismissError ? (
      <Alert severity="error" style={{ marginTop: 12 }}>
        {dismissError}
      </Alert>
    ) : null}
    <div style={buttonRowStyles}>
      <Button
        aria-label="Dismiss"
        disabled={!onDismiss || isDismissing}
        size="small"
        style={dismissButtonStyles}
        variant="outlined"
        onClick={onDismiss}
      >
        Got it
      </Button>
    </div>
  </section>
);
